import { Show, type JSX } from 'solid-js'
import shopSvg from '~/assets/figma/shop.svg'
import { m } from '~/paraglide/messages'

export type TabValue = 'baking' | 'collection'

export function TabBar(props: {
  active: TabValue
  onChange: (value: TabValue) => void
  flashCollection?: boolean
  onOpenShop?: () => void
}) {
  return (
    <div class="flex items-center gap-2 max-[375px]:-mt-11 max-[375px]:flex-col max-[375px]:items-end max-[375px]:gap-1">
      <div role="tablist" class="flex items-center gap-2 max-[375px]:order-2">
        <TabItem
          value="baking"
          active={props.active === 'baking'}
          onSelect={props.onChange}
          label={m.tab_baking_area()}
        >
          <BakingIcon />
        </TabItem>
        <TabItem
          value="collection"
          active={props.active === 'collection'}
          onSelect={props.onChange}
          label={m.tab_my_collection()}
          flash={
            props.flashCollection === true && props.active !== 'collection'
          }
        >
          <CollectionIcon />
        </TabItem>
      </div>
      <Show when={props.onOpenShop !== undefined}>
        <button
          type="button"
          aria-label={m.gem_store()}
          onClick={() => props.onOpenShop?.()}
          class="relative grid size-10 shrink-0 place-items-center rounded-full bg-bakery-shop backdrop-blur-md transition-transform duration-200 [box-shadow:0_-3px_5.8px_rgba(214,117,119,0.4)] active:scale-[0.95] max-[375px]:order-1"
        >
          <img src={shopSvg} alt="" aria-hidden="true" class="size-6" />
        </button>
      </Show>
    </div>
  )
}

function TabItem(props: {
  value: TabValue
  active: boolean
  onSelect: (v: TabValue) => void
  label: string
  flash?: boolean
  children: JSX.Element
}) {
  return (
    <button
      role="tab"
      type="button"
      aria-selected={props.active}
      onClick={() => props.onSelect(props.value)}
      class="flex h-10 w-40 items-center justify-center gap-2 rounded-2xl px-1 backdrop-blur-md transition-all [transition-duration:200ms] active:scale-[0.97]"
      classList={{
        'bg-white text-bakery-brown-deep [box-shadow:0_-3px_5.8px_rgba(253,240,175,0.6)]':
          props.active,
        'bg-black/15 text-white': !props.active,
        'animate-pulse-ring': props.flash === true,
      }}
    >
      <span class="grid size-6 place-items-center">{props.children}</span>
      <span class="text-sm font-bold leading-5">{props.label}</span>
    </button>
  )
}

function BakingIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      class="size-6"
      fill="currentColor"
      aria-hidden="true"
    >
      <g transform="translate(5.5 1)">
        <path d="M1.24006 0.00282317C2.10415 0.0650931 2.37339 1.10866 2.63897 1.78076C3.28178 3.40734 2.61176 4.74328 1.72772 6.10819C1.04412 7.16368 3.08181 8.48164 1.35887 8.99802C0.629329 8.91823 0.427719 8.27224 0.183117 7.65813C-0.365704 6.28007 0.433619 5.24059 1.08369 4.10701C1.70613 3.0217 1.04515 2.05229 0.602307 1.07423C0.366416 0.531836 0.746409 0.150327 1.24006 0.00282317Z" />
        <path d="M6.35107 0.00282317C7.15615 0.109612 7.46922 1.20258 7.72455 1.86923C8.31584 3.41438 7.65721 4.76314 6.8126 6.04583C6.06783 7.17683 8.14978 8.47154 6.41293 9.00023C5.56931 8.91297 5.26171 7.83579 5.11148 7.11644C4.77303 5.49592 6.2811 4.64236 6.38689 3.10284C6.46164 2.01462 4.6263 0.379777 6.35107 0.00282317Z" />
        <path d="M11.3604 0.000225457C12.1364 0.0693047 12.3916 0.912439 12.6637 1.53439C13.1689 2.68906 13.1109 3.97466 12.4503 5.05231C12.2301 5.41152 11.9871 5.81716 11.7921 6.19346C11.2578 7.26947 13.1119 8.36333 11.5214 8.99401C10.7459 8.95114 10.5696 8.33877 10.316 7.70236C9.74695 6.27409 10.5237 5.26241 11.2026 4.08887C11.807 3.04413 11.1518 2.05105 10.7315 1.10188C10.4889 0.523368 10.8327 0.157215 11.3604 0.000225457Z" />
      </g>
      <g transform="translate(1 12.14)">
        <path d="M18.9434 7.5957C18.6054 8.92575 17.4075 9.85645 16.0352 9.85645H6.96582C5.59363 9.85635 4.39656 8.92562 4.05859 7.5957L2.62988 1.97168H20.3721L18.9434 7.5957ZM22.0137 0C22.558 2.74112e-05 22.999 0.441 22.999 0.985352C22.999 1.52965 22.558 1.97068 22.0137 1.9707H0.986328C0.441997 1.9707 6.10772e-05 1.52967 0 0.985352C0 0.440983 0.44196 0 0.986328 0H22.0137Z" />
      </g>
    </svg>
  )
}

function CollectionIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      class="size-6"
      fill="currentColor"
      aria-hidden="true"
    >
      <g transform="translate(2.25 2.44)">
        <path d="M2.65601e-07 17.5629V9.57269C-0.000189282 9.14218 0.101077 8.71783 0.294922 8.33343C0.488848 7.94892 0.769773 7.61463 1.11621 7.35882L4.50488 4.85882C4.83816 4.6131 5.30783 4.68383 5.55371 5.01702C5.7996 5.35025 5.72862 5.81987 5.39551 6.06585L2.00781 8.56585C1.85041 8.68208 1.72193 8.83355 1.63379 9.00823C1.54571 9.18288 1.49996 9.37611 1.5 9.57171V17.5629C1.5001 17.6291 1.52645 17.6929 1.57324 17.7397C1.62011 17.7864 1.68378 17.8129 1.75 17.8129H17.75C17.8162 17.8129 17.8799 17.7864 17.9268 17.7397C17.9736 17.6929 17.9999 17.6291 18 17.5629V8.90863C18 8.14413 17.4983 6.79589 16.4258 5.34027C15.3751 3.91443 13.853 2.49235 11.9746 1.55316C11.9621 1.5469 11.9496 1.54057 11.9375 1.53363C11.8958 1.50976 11.8478 1.4979 11.7998 1.50042C11.776 1.5017 11.7528 1.50723 11.7305 1.51507L11.667 1.5473L8.58887 3.77093C8.25317 4.0131 7.78447 3.93749 7.54199 3.60199C7.29947 3.26619 7.37514 2.79665 7.71094 2.55413L10.7891 0.331477L10.8936 0.261165C11.1426 0.107361 11.4271 0.017917 11.7207 0.00237585C12.0423 -0.0145655 12.3617 0.0594747 12.6445 0.212337C14.766 1.27307 16.4644 2.86357 17.6338 4.45062C18.7813 6.00799 19.5 7.67312 19.5 8.90863V17.5629C19.4999 18.0269 19.3154 18.4721 18.9873 18.8002C18.6591 19.1283 18.214 19.3129 17.75 19.3129H1.75C1.28596 19.3129 0.840867 19.1283 0.512696 18.8002C0.184595 18.4721 9.97468e-05 18.0269 2.65601e-07 17.5629Z" />
      </g>
      <g transform="translate(6.25 4.25)">
        <path d="M4 2.75C4 2.05964 3.44036 1.5 2.75 1.5C2.05964 1.5 1.5 2.05964 1.5 2.75C1.5 3.44036 2.05964 4 2.75 4C3.44036 4 4 3.44036 4 2.75ZM5.5 2.75C5.5 4.26878 4.26878 5.5 2.75 5.5C1.23122 5.5 0 4.26878 0 2.75C0 1.23122 1.23122 0 2.75 0C4.26878 0 5.5 1.23122 5.5 2.75Z" />
      </g>
      <g transform="translate(2.25 12.25)">
        <path d="M13.75 0C14.1642 0 14.5 0.335786 14.5 0.75C14.5 1.16421 14.1642 1.5 13.75 1.5H0.75C0.335786 1.5 0 1.16421 0 0.75C0 0.335786 0.335786 0 0.75 0H13.75Z" />
      </g>
      <g transform="translate(2.25 16.25)">
        <path d="M13.75 0C14.1642 0 14.5 0.335786 14.5 0.75C14.5 1.16421 14.1642 1.5 13.75 1.5H0.75C0.335786 1.5 0 1.16421 0 0.75C0 0.335786 0.335786 0 0.75 0H13.75Z" />
      </g>
    </svg>
  )
}
